import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Alert, Button } from "@cmsgov/ds-medicare-gov";
import { AppContext, defaultState } from "../../../app/store";
import { ActionType, PlanCategoryFilters, PlanType } from "../../../@types";
import {
  doFiltersExist,
  isMedicareAdvantageType,
  useTranslate,
} from "../../../helpers";
import { ChangeLocation } from "../../../components";
import CarrierFilter from "./CarrierFilter";
import DrugCoverageFilter from "./DrugCoverageFilter";
import PlanCategoriesFilter from "./PlanCategoriesFilter";
import PlanCoveragesFilter from "./PlanCoveragesFilter";
import SpecialNeedsPlanFilter from "./SpecialNeedsPlansFilter";
import StarRatingsFilter from "./StarRatingsFilter";
import FilterChips from "./FilterChips";
import { updateFilter, updateSNPFilters } from "./helpers";
import { default as C } from "./constants";
import PlanFiltersHelpDrawer from "./PlanFiltersHelpDrawer";

interface PlanFiltersContainerProps {
  heading: React.ReactNode;
  setIgnoreHeaderFocus: (ignore: boolean) => void;
  resetToFirstPageOfResults: () => void;
  numPlans: number;
  loadingPlans: boolean;
  availableCategories?: PlanCategoryFilters;
  swapPlanType: () => void;
}

const PlanFiltersContainer: FunctionComponent<PlanFiltersContainerProps> = props => {
  const t = useTranslate();
  const context = useContext(AppContext);
  const {
    state: {
      searchResultsFilters = defaultState.searchResultsFilters,
      planType,
    },
    dispatch,
  } = context;
  const [catsToApplyAfterSwap, setCatsToApplyAfterSwap] = useState<string[]>(
    []
  );
  const filtersExist = doFiltersExist(searchResultsFilters);
  const filtersAreActive = !props.loadingPlans && filtersExist;
  const isMA = isMedicareAdvantageType(planType || PlanType.MAPD);

  useEffect(() => {
    if (catsToApplyAfterSwap.length && planType !== PlanType.PDP) {
      props.resetToFirstPageOfResults();
      updateFilter(
        context,
        C.SearchFilterKeys.planCategoriesFilter,
        catsToApplyAfterSwap
      );
      setCatsToApplyAfterSwap([]);
    }
  }, [planType]);

  if (!searchResultsFilters.planCoveragesFilter) {
    return null;
  }

  return (
    <div className="PlanFilters">
      <div className="PlanFilters__header">
        <div>{!props.loadingPlans && props.heading}</div>
      </div>

      <div className="PlanFilters__header">
        <div className="PlanFilters__headerActions">
          <div className="PlanFilters__action">
            <p className="PlanFilters__action__label">
              {t("search_results_page.my_location")}
            </p>
            <div className="CCXPBase__button width-bug">
              <ChangeLocation
                showLocation={true}
                tealiumOpenProps={{
                  event_action: "Find Plans - Available Plans",
                  event_label: "Change Location",
                }}
              />
            </div>
          </div>
          <div className="PlanFilters__action" data-testid="type-dropdown">
            <PlanCategoriesFilter
              categories={props.availableCategories}
              onFilterUpdate={(value, swapType): void => {
                props.setIgnoreHeaderFocus(true);

                if (swapType) {
                  // set the categories to be applied after the plan swap happens
                  setCatsToApplyAfterSwap(value);
                  props.swapPlanType();
                } else {
                  props.resetToFirstPageOfResults();
                  updateFilter(
                    context,
                    C.SearchFilterKeys.planCategoriesFilter,
                    value
                  );
                }
              }}
            />
          </div>
        </div>
      </div>

      {props.numPlans === 0 && filtersAreActive && (
        <Alert
          className="PlanFilters__noPlansWarning animated fadeIn"
          variation="warn"
          heading={t("search_results_page.no_plans_available")}
        >
          {t("search_results_page.no_plans_with_filters")}{" "}
          <Button
            variation="transparent"
            className="ds-u-padding--0"
            onClick={(): void => {
              dispatch({
                type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
                payload: defaultState.searchResultsFilters,
              });
              props.resetToFirstPageOfResults();
            }}
          >
            {t("search_results_page.clear_all_filters")}
          </Button>{" "}
          {t("search_results_page.remove_filters_individually")}
        </Alert>
      )}

      {props.numPlans === 0 && !filtersAreActive && !props.loadingPlans && (
        <Alert
          className="PlanFilters__noPlansWarning animated fadeIn"
          variation="warn"
          heading={t("search_results_page.no_plans_available")}
        >
          {t("search_results_page.no_plans_for_area")} <ChangeLocation inline />{" "}
          {t("search_results_page.change_loc_to_find_plans")}
        </Alert>
      )}

      <div className="FilterBar">
        <div className="ds-u-padding-bottom--0 ds-u-padding-top--2 ds-u-padding-x--2 ds-u-md-padding-x--7 ds-u-display--flex ds-u-flex-wrap--wrap ds-u-xl-flex-wrap--nowrap">
          <h2 className="FilterBar__heading">
            {t("search_results_page.filter_by")}:
          </h2>
          <div className="mct-u-md-width--full">
            <div className="FilterBar__filters">
              <PlanCoveragesFilter
                onFilterUpdate={(newFilterValue): void => {
                  props.setIgnoreHeaderFocus(true);
                  props.resetToFirstPageOfResults();
                  updateFilter(
                    context,
                    C.SearchFilterKeys.planCoveragesFilter,
                    newFilterValue
                  );
                }}
              />
              <CarrierFilter
                value={searchResultsFilters.insuranceCarrierFilter}
                options={searchResultsFilters.insuranceCarriers.map(
                  carrier => ({
                    label: carrier,
                    value: carrier,
                  })
                )}
                onFilterUpdate={(newFilterValue): void => {
                  props.setIgnoreHeaderFocus(true);
                  props.resetToFirstPageOfResults();
                  updateFilter(
                    context,
                    C.SearchFilterKeys.insuranceCarrierFilter,
                    newFilterValue
                  );
                }}
              />
              {planType !== PlanType.PDP && (
                <DrugCoverageFilter
                  onFilterUpdate={(newFilterValue): void => {
                    props.setIgnoreHeaderFocus(true);
                    props.resetToFirstPageOfResults();
                    updateFilter(
                      context,
                      C.SearchFilterKeys.drugCoverageFilter,
                      newFilterValue
                    );
                  }}
                />
              )}
              <StarRatingsFilter
                value={searchResultsFilters.starRatingFilter}
                onFilterUpdate={(newFilterValue: string): void => {
                  props.setIgnoreHeaderFocus(true);
                  props.resetToFirstPageOfResults();
                  updateFilter(
                    context,
                    C.SearchFilterKeys.starRatingFilter,
                    newFilterValue
                  );
                }}
              />
              {isMA && (
                <SpecialNeedsPlanFilter
                  onFilterUpdate={(newFilterValues: string[]): void => {
                    props.setIgnoreHeaderFocus(true);
                    props.resetToFirstPageOfResults();
                    updateSNPFilters(context, newFilterValues);
                  }}
                />
              )}
              <div className="FilterBar__toggleContainer">
                <PlanFiltersHelpDrawer {...props} />
              </div>
            </div>

            <FilterChips pageReset={props.resetToFirstPageOfResults} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFiltersContainer;
